import React, { useState } from 'react';
import { Modal, Input, Form, Button } from 'antd';
import { useTranslation } from 'react-i18next';

interface SaveTemplateModalProps {
  visible: boolean;
  onSave: (templateName: string) => Promise<void>;
  onCancel: () => void;
  onImportWithoutSaving: () => Promise<void>;
  isLoading?: boolean;
}

const SaveTemplateModal: React.FC<SaveTemplateModalProps> = ({
  visible,
  onSave,
  onCancel,
  onImportWithoutSaving,
  isLoading = false,
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [isSaving, setIsSaving] = useState(false);
  const [isImporting, setIsImporting] = useState(false);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setIsSaving(true);
      await onSave(values.templateName);
      form.resetFields();
      setIsSaving(false);
    } catch (error) {
      setIsSaving(false);
      // Form validation error or save error - form will show validation errors
      if (error && typeof error === 'object' && 'errorFields' in error) {
        // Validation error, form will handle it
        return;
      }
      // Save error will be handled by parent
    }
  };

  const handleImportWithoutSaving = async () => {
    setIsImporting(true);
    try {
      await onImportWithoutSaving();
      form.resetFields();
    } catch (error) {
      // Error handled by parent
    } finally {
      setIsImporting(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  return (
    <Modal
      title={t('visaApplications.bulkImport.saveTemplate.title') || 'Save Template'}
      open={visible}
      onCancel={handleCancel}
      footer={[
        <Button key="cancel" onClick={handleCancel} disabled={isSaving || isImporting}>
          {t('common.cancel')}
        </Button>,
        <Button
          key="import-without-saving"
          onClick={handleImportWithoutSaving}
          loading={isImporting}
          disabled={isSaving || isLoading}
        >
          {t('visaApplications.bulkImport.saveTemplate.importWithoutSaving') ||
            'Import Without Saving'}
        </Button>,
        <Button
          key="save"
          type="primary"
          onClick={handleSave}
          loading={isSaving || isLoading}
          disabled={isImporting}
        >
          {t('visaApplications.bulkImport.saveTemplate.saveAndImport') || 'Save & Import'}
        </Button>,
      ]}
      width={500}
    >
      <div className="mb-3">
        <p>
          {t('visaApplications.bulkImport.saveTemplate.description') ||
            'Do you want to save this mapping as a template?'}
        </p>
      </div>

      <Form form={form} layout="vertical">
        <Form.Item
          name="templateName"
          label={t('visaApplications.bulkImport.saveTemplate.templateNameLabel') || 'Template Name'}
          rules={[
            {
              required: true,
              message:
                t('visaApplications.bulkImport.saveTemplate.templateNameRequired') ||
                'Template name is required',
            },
            {
              min: 1,
              max: 100,
              message:
                t('visaApplications.bulkImport.saveTemplate.templateNameLength') ||
                'Template name must be between 1 and 100 characters',
            },
          ]}
        >
          <Input
            placeholder={
              t('visaApplications.bulkImport.saveTemplate.templateNamePlaceholder') ||
              'Enter template name'
            }
            size="large"
            autoFocus
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default SaveTemplateModal;
